import type { Me } from '@myrewards/contracts';
import { matchRoutes, type RouteObject } from 'react-router';
import { LEVEL_ONE, type LevelOneId, type PageHelp } from '../shell';
import type { PageSearch } from '../search/pages';

// Where a page sits in Level 1 (QA-045, APP-082): every route names the Level 1 item it belongs
// to, so the header marks it current wherever the person is: the queue and a check under Manage
// (APP-234), My drafts & submitted under Recognize. Approvals belong to the area of the person
// deciding (an Executive's and a Sector Lead's under Manage, APP-236), a Spot nomination's own
// page to where it was opened from, and the award record wherever it was opened from, the same
// place its back link goes.

/** What a page that's reached from several places needs to work out where it belongs. */
export interface NavContext {
  /** The history entry's state: `back` when the opening page said where it came from. */
  state: unknown;
  /** The route's loader data; undefined when asked about another page. */
  data: unknown;
  me: Me;
  /** The Level 1 item another page belongs to, from the route tree. */
  ownerOf: (path: string) => LevelOneId | null;
}

/**
 * A route's Level 1 item. `null` only for pages outside the menus (the not-found page, the
 * development gallery); a function for a page that belongs to different items for different
 * people or visits (approvals by who decides, the record by where it was opened from).
 */
export type NavItem = LevelOneId | null | ((at: NavContext) => LevelOneId | null);

/** The `navItem` a route's handle names, or undefined when it names none. */
export function navItemOf(handle: unknown): NavItem | undefined {
  if (typeof handle !== 'object' || handle === null || !('navItem' in handle)) return undefined;
  return (handle as { navItem: NavItem }).navItem;
}

/** Whether a route's handle says its page belongs where it was opened from (the record). */
function opensFromHandle(handle: unknown): boolean {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    'opensFrom' in handle &&
    handle.opensFrom === true
  );
}

/**
 * The Level 1 item a path belongs to for this person: the deepest route that names one. A page
 * that decides is asked with no history state and no loader data, and can't ask about a third
 * page, so asking never loops.
 */
export function ownerOfPath(routes: RouteObject[], path: string, me?: Me): LevelOneId | null {
  const item = itemAt(routes, path);
  if (item === undefined) return null;
  if (typeof item !== 'function') return item;
  return me === undefined ? null : item({ state: null, data: undefined, me, ownerOf: () => null });
}

/**
 * Whether a path's page belongs where it was opened from (the award record), so a link to it
 * carries where it was opened from (APP-082).
 */
export function opensFrom(routes: RouteObject[], path: string): boolean {
  const pathname = path.split(/[?#]/)[0] ?? path;
  return (matchRoutes(routes, pathname) ?? []).some((m) => opensFromHandle(m.route.handle));
}

/** The `navItem` of the deepest route a path matches that names one. */
function itemAt(routes: RouteObject[], path: string): NavItem | undefined {
  const pathname = path.split(/[?#]/)[0] ?? path;
  const matches = matchRoutes(routes, pathname) ?? [];
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const item = navItemOf(matches[i]?.route.handle);
    if (item !== undefined) return item;
  }
  return undefined;
}

/** The Level 1 item the current page belongs to: the deepest matched route that names one. */
export function currentNavItem(
  matches: readonly { handle: unknown; loaderData: unknown }[],
  at: Omit<NavContext, 'data'>,
): LevelOneId | null {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const item = navItemOf(match?.handle);
    if (item === undefined) continue;
    return typeof item === 'function' ? item({ ...at, data: match?.loaderData }) : item;
  }
  return null;
}

/**
 * The Level 1 item the page a record was opened from had current, as its link said
 * (`state.back.item`): only an item this person has, so a stale or crafted state can't light
 * one they can't use.
 */
export function itemFromState(state: unknown, me: Pick<Me, 'nav'>): LevelOneId | null {
  if (typeof state !== 'object' || state === null || !('back' in state)) return null;
  const back = state.back;
  if (typeof back !== 'object' || back === null || !('item' in back)) return null;
  const item = back.item;
  const known = LEVEL_ONE.find((entry) => entry.id === item)?.id;
  return known !== undefined && me.nav.includes(known) ? known : null;
}

/** Whether the current page (its matched routes) belongs where it was opened from. */
export function isOpenedFrom(matches: readonly { handle: unknown }[]): boolean {
  return matches.some((m) => opensFromHandle(m.handle));
}

/**
 * Names the Level 1 item for every route of an area that doesn't name its own: the router lists
 * each area once with its owner (the Stock Admin queue under Manage, Settings' pages under Settings).
 */
export function withNavItem(item: LevelOneId, routes: readonly RouteObject[]): RouteObject[] {
  return routes.map((route): RouteObject => {
    const handle: unknown =
      navItemOf(route.handle) === undefined
        ? { ...(route.handle as object | undefined), navItem: item }
        : route.handle;
    return {
      ...route,
      handle,
      ...(route.children !== undefined && { children: withNavItem(item, route.children) }),
    } as RouteObject;
  });
}

/**
 * The words a Help title adds around a page's name: its Level 1 area ("Admin · Sent mail") or
 * the role it's for ("Work queue · Stock Admin").
 */
const AREA_OR_ROLE = new Set([
  ...LEVEL_ONE.map((item) => item.label),
  // APP-236: no longer a Level 1 item, but still the Executive group's name in Help titles
  // ("Executive · Budget pools"), so a back link reads "Back to Budget pools".
  'Executive',
  'Stock Admin',
  'System Admin',
  'System administration',
  'Sector Lead',
  'Payroll',
  'Finance',
  'Executive delegate',
  'Manager',
  'Delegate',
]);

/**
 * A page's own name, for the record's back link ("Back to Everything in progress"): its name in
 * Search & jump when it has one, else its Help title without the area or role around it.
 */
export function pageNameOf(handle: unknown, me: Me | undefined): string | null {
  if (typeof handle !== 'object' || handle === null) return null;
  const { search, help } = handle as {
    search?: PageSearch | readonly PageSearch[];
    help?: PageHelp | ((me: Me) => PageHelp);
  };
  if (search !== undefined && !Array.isArray(search)) {
    const label = (search as PageSearch).label.trim();
    if (label !== '') return label;
  }
  const page = typeof help === 'function' ? (me === undefined ? undefined : help(me)) : help;
  const parts = (page?.pageLabel ?? '')
    .split(' · ')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  return parts.find((part) => !AREA_OR_ROLE.has(part)) ?? parts[0] ?? null;
}
